<script lang="ts" setup>
import HelpWidget from '@/app/common/HelpWidget.vue';
import NotificationLayer from '@/app/common/NotificationLayer.vue';
import ToastAlertLayer from '@/app/common/ToastAlertLayer.vue';
import TopHeader from '@/app/common/TopHeader.vue';
import WebSocketLayer from '@/app/common/WebSocketLayer.vue';
import ZoomWarningLayer from '@/app/common/ZoomWarningLayer.vue';
import FeatureRankingPopup from '@/app/feature-ranking/FeatureRankingPopup.vue';
import { useAIAssistantContext } from '@/composables/useAIAssistantContext';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';

const AIChatDockPanel = defineAsyncComponent(
  () => import('@/components/ai/AIChatDockPanel.vue'),
);

const router = useRouter();
const uiStore = useUiStore();
const authStore = useAuthStore();

// The AI assistant docks next to every page of a project or template (WI
// #5769): it pushes the page aside instead of covering it, does not scroll
// with it, and stays the same panel — the same chat — while the user moves
// between map, list, kanban and the other pages of that project/template.
const {
  mode: aiMode,
  contextId: aiContextId,
  isDockOpen: isAIDockOpen,
} = useAIAssistantContext();

const aiDock = computed(() =>
  authStore.hasTokens && isAIDockOpen.value && aiMode.value && aiContextId.value
    ? { mode: aiMode.value, contextId: aiContextId.value }
    : null,
);

const smallPaddingPageRoutes = [
  'ProjectMapTemplateMap',
  'ProjectPage',
  'Remap',
  'RemapProject',
  'ViewResults',
  'ManagedViewResults',
];

const isSmallPaddingPage = computed(() => {
  if (typeof router.currentRoute.value.name !== 'string') {
    return false;
  }

  return smallPaddingPageRoutes.includes(router.currentRoute.value.name);
});

uiStore.initializeKeyMapping();
</script>

<template>
  <div class="app">
    <div class="screen">
      <transition name="fade">
        <div v-if="!$route.meta.hideSideNavigation" class="side-navigation">
          <AppSideNavigation v-if="authStore.hasTokens" />
        </div>
      </transition>

      <div ref="viewportRef" class="viewport">
        <div
          :class="{ 'small-padding': isSmallPaddingPage }"
          class="content page-content"
        >
          <router-view v-slot="{ Component }">
            <div class="transition-container">
              <TopHeader v-if="authStore.hasTokens" />

              <transition name="fade" mode="out-in">
                <component :is="Component" />
              </transition>
            </div>
          </router-view>
        </div>
      </div>

      <AIChatDockPanel
        v-if="aiDock"
        :key="`${aiDock.mode}-${aiDock.contextId}`"
        :mode="aiDock.mode"
        :context-id="aiDock.contextId"
      />
    </div>

    <UiUnlockLayer />
    <UiConfirmLayer />
    <ToastAlertLayer />
    <!-- real-time notifications layer -->
    <div v-if="!$route.meta.hideSideNavigation && authStore.hasTokens">
      <NotificationLayer />
      <WebSocketLayer />
    </div>
    <!-- browser zoom warning layer -->
    <ZoomWarningLayer v-if="authStore.hasTokens" />
    <!-- floating help widget -->
    <HelpWidget v-if="authStore.hasTokens" />
    <!-- feature ranking popup (once per day for users with role) -->
    <FeatureRankingPopup v-if="authStore.hasTokens" />
  </div>
</template>

<style lang="scss">
$link: var(--color-primary);

@use 'sass:meta';

// The CSS layer `base` holds the browser normalisation, the element resets and
// the focus ring, under PrimeVue's layer `primevue` (main.ts) and under every
// other app style: PrimeVue's components draw over the resets, and the app's
// own rules win over both. The order inside the layer is the load order here.
@layer base {
  @include meta.load-css(
    '../../node_modules/modern-normalize/modern-normalize.css'
  );
}
@include meta.load-css('../theme/styles/reset.scss');
@include meta.load-css('../theme/styles/main.scss');

.app {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;

  font-family: 'Roboto', sans-serif;

  background-color: var(--color-gray-100);
}

.screen {
  display: flex;

  width: 100vw;
}

.side-navigation {
  background-color: var(--color-gray-0);
}

.viewport {
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
@layer base {
  button {
    border: transparent;
  }

  // The one keyboard focus ring, on every control (WI 5799). A mouse click shows
  // it only in text fields, where the browser matches :focus-visible on any
  // focus. A component whose own reset (all: unset, outline: none) outranks this
  // rule draws it with the focus-ring mixin from helpers.scss; PrimeVue draws
  // its own from the preset's focusRing tokens (main.ts).
  :focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }
}

.top-navigation {
  flex: 0 0 auto;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ui-spinner {
    color: var(--color-gray-600);
  }
}

.content {
  flex: 1 1 auto;

  display: flex;
}

.page-content {
  transition: padding var(--ease-standard) 600ms;
  padding: var(--space-16) var(--space-24) var(--space-24) var(--space-24);
  flex: 1;
  min-height: 0;

  &.small-padding {
    padding: var(--space-16);
  }
}

.transition-container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  max-width: 100%;
}
</style>
